/**
 * Контроллер для управления пользовательскими настройками
 * Использует localStorage для хранения настроек между сессиями
 */
class UserSettingsController {
  constructor() {
    this.storageKey = 'kampus_user_settings';
    this.notificationCountKey = 'kampus_notification_count';

    // Значения по умолчанию
    this.defaultSettings = {
      // Основные настройки уведомлений
      hideInitNotify: false,
      maxNotificationShows: 3,
      autoHideNotifications: true,
      notificationSound: true,

      // Настройки темы и интерфейса
      isBlackTheme: false,
      showNotificationTime: true,

      // Настройки пользовательского интерфейса
      language: 'ru',
      timezone: 'Europe/Moscow',
      dateFormat: 'DD.MM.YYYY',
      timeFormat: '24h',
    };

    this.notificationShowCount = this.getNotificationCount();
  }

  /**
   * Получить все настройки с применением дефолтных значений
   * @returns {Object} Объект со всеми настройками
   */
  getSettings() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      const settings = stored ? JSON.parse(stored) : {};

      // Объединяем с дефолтными значениями
      return { ...this.defaultSettings, ...settings };
    } catch (error) {
      console.error('Error loading user settings:', error);
      return this.defaultSettings;
    }
  }

  /**
   * Получить конкретную настройку
   * @param {string} key - Ключ настройки
   * @returns {*} Значение настройки или дефолтное значение
   */
  getSetting(key) {
    const settings = this.getSettings();
    return settings[key] !== undefined ? settings[key] : this.defaultSettings[key];
  }

  /**
   * Сохранить одну настройку
   * @param {string} key - Ключ настройки
   * @param {*} value - Значение настройки
   * @returns {boolean} Успешность операции
   */
  setSetting(key, value) {
    try {
      const settings = this.getSettings();
      settings[key] = value;
      localStorage.setItem(this.storageKey, JSON.stringify(settings));

      // Эмитируем событие для обновления компонентов
      this.emitSettingsChange(key, value);

      return true;
    } catch (error) {
      console.error('Error saving user setting:', error);
      return false;
    }
  }

  /**
   * Сохранить несколько настроек одновременно
   * @param {Object} newSettings - Объект с настройками
   * @returns {boolean} Успешность операции
   */
  setSettings(newSettings) {
    try {
      const settings = this.getSettings();
      const updatedSettings = { ...settings, ...newSettings };
      localStorage.setItem(this.storageKey, JSON.stringify(updatedSettings));

      // Эмитируем событие для обновления компонентов
      this.emitSettingsChange('multiple', newSettings);

      return true;
    } catch (error) {
      console.error('Error saving user settings:', error);
      return false;
    }
  }

  /**
   * Сбросить все настройки к дефолтным значениям
   * @returns {boolean} Успешность операции
   */
  resetSettings() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.notificationCountKey);
      this.notificationShowCount = { currentId: null, count: 0 };

      // Эмитируем событие для обновления компонентов
      this.emitSettingsChange('reset', this.defaultSettings);

      return true;
    } catch (error) {
      console.error('Error resetting user settings:', error);
      return false;
    }
  }

  /**
   * Получить данные о счетчике уведомлений
   * @returns {Object} Объект с информацией о текущем уведомлении и количестве показов
   */
  getNotificationCount() {
    try {
      const stored = localStorage.getItem(this.notificationCountKey);
      return stored ? JSON.parse(stored) : { currentId: null, count: 0 };
    } catch (error) {
      console.error('Error loading notification count:', error);
      return { currentId: null, count: 0 };
    }
  }

  /**
   * Увеличить счетчик показов уведомления
   * @param {string|number} notificationId - ID уведомления
   * @returns {number} Текущее количество показов
   */
  incrementNotificationCount(notificationId) {
    try {
      if (this.notificationShowCount.currentId !== notificationId) {
        // Новое уведомление - сбрасываем счетчик
        this.notificationShowCount = { currentId: notificationId, count: 1 };
      } else {
        // То же уведомление - увеличиваем счетчик
        this.notificationShowCount.count += 1;
      }

      localStorage.setItem(this.notificationCountKey, JSON.stringify(this.notificationShowCount));
      return this.notificationShowCount.count;
    } catch (error) {
      console.error('Error updating notification count:', error);
      return 0;
    }
  }

  /**
   * Проверить, нужно ли скрыть уведомление
   * @param {string|number} notificationId - ID уведомления
   * @returns {boolean} true если уведомление нужно скрыть
   */
  shouldHideNotification(notificationId) {
    const maxShows = this.getSetting('maxNotificationShows');
    const hideInitNotify = this.getSetting('hideInitNotify');
    const isDevelopment = this.isDevelopmentEnvironment();

    // Скрыть если это локальная разработка
    if (isDevelopment) {
      console.log('Hiding notification due to development environment');
      return true;
    }

    // Скрыть если пользователь отключил начальные уведомления
    if (hideInitNotify) {
      console.log('Hiding notification due to hideInitNotify setting');
      return true;
    }

    // Скрыть если уведомление показывалось больше максимального количества раз
    if (this.notificationShowCount.currentId === notificationId && this.notificationShowCount.count >= maxShows) {
      console.log(`Hiding notification due to max shows limit (${maxShows})`);
      return true;
    }

    return false;
  }

  /**
   * Проверить, является ли текущая среда разработческой
   * @returns {boolean} true если среда разработки
   */
  isDevelopmentEnvironment() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === 'kampus.local' || hostname === '127.0.0.1';
  }

  /**
   * Эмитировать событие изменения настроек для обновления компонентов
   * @param {string} key - Ключ изменившейся настройки
   * @param {*} value - Новое значение
   */
  emitSettingsChange(key, value) {
    try {
      const event = new CustomEvent('kampus-settings-changed', {
        detail: { key, value, allSettings: this.getSettings() },
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error emitting settings change event:', error);
    }
  }

  /**
   * Подписаться на изменения настроек
   * @param {Function} callback - Функция обратного вызова
   * @returns {Function} Функция для отписки
   */
  onSettingsChange(callback) {
    const handler = event => {
      callback(event.detail);
    };

    window.addEventListener('kampus-settings-changed', handler);

    // Возвращаем функцию для отписки
    return () => {
      window.removeEventListener('kampus-settings-changed', handler);
    };
  }

  /**
   * Получить настройки для экспорта/импорта
   * @returns {Object} Настройки в JSON формате
   */
  exportSettings() {
    return {
      settings: this.getSettings(),
      notificationCount: this.getNotificationCount(),
      exportDate: new Date().toISOString(),
    };
  }

  /**
   * Импортировать настройки
   * @param {Object} data - Объект с настройками
   * @returns {boolean} Успешность операции
   */
  importSettings(data) {
    try {
      if (data.settings) {
        this.setSettings(data.settings);
      }

      if (data.notificationCount) {
        localStorage.setItem(this.notificationCountKey, JSON.stringify(data.notificationCount));
        this.notificationShowCount = data.notificationCount;
      }

      return true;
    } catch (error) {
      console.error('Error importing settings:', error);
      return false;
    }
  }
}

// Создаем singleton экземпляр
const userSettingsController = new UserSettingsController();

export default userSettingsController;
